import { BaseApi } from '@Features/Base/BaseApi/BaseApi'
import { dashboardAPI } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import CourseApi from '@Features/Course/APIs/CourseApi/CourseApi'
import { toQueryString as toQS } from '@Utilities/toQueryString'

export type StandaloneCatalogArgs = {
	page?: string | number
	perPage?: number
	query?: string
}

export type CheckoutStandaloneCoursePayload = {
	/** field name per the standalone contract — confirm against the live API */
	standalone_course_id: number
	payment_method: 'wallet' | 'scratchCard' | 'free'
	promocode?: string
	/** scratch-card code */
	code?: string
}

/**
 * Standalone courses — the additive /standalone-courses namespace. Catalog,
 * featured (admin-curated), details (public), mine (owned), access status,
 * start-lecture (creates the enroll row; consumption then goes through the
 * EXISTING /course/:id + unit endpoints), checkout, and standalone invoices.
 */
export const standaloneCourseApi = BaseApi.enhanceEndpoints({
	addTagTypes: ['STANDALONE_COURSE', 'STANDALONE_MINE', 'STANDALONE_FEATURED'],
}).injectEndpoints({
	endpoints: builder => ({
		getStandaloneCourses: builder.query<ApiResponse<StandaloneCourse[]>, StandaloneCatalogArgs>({
			query: args => `/standalone-courses?${toQS(args)}`,
		}),
		getFeaturedStandaloneCourses: builder.query<ApiResponse<StandaloneCourse[]>, void>({
			query: () => '/standalone-courses/featured',
			providesTags: ['STANDALONE_FEATURED'],
		}),
		getStandaloneCourseDetails: builder.query<ApiResponse<StandaloneCourse>, string>({
			query: identifier => `/standalone-courses/details/${identifier}`,
			providesTags: result => (result ? [{ type: 'STANDALONE_COURSE' as const, id: result.data.id }] : ['STANDALONE_COURSE']),
		}),
		// Returns ENROLLMENT rows (course nested under `standaloneCourse`), paginated.
		getMyStandaloneCourses: builder.query<ApiResponse<StandaloneCourseEnrollment[]>, { page?: number; perPage?: number } | void>({
			query: args => `/standalone-courses/mine?${toQS({ page: 1, perPage: 50, ...(args ?? {}) })}`,
			providesTags: ['STANDALONE_MINE'],
		}),
		getStandaloneCourseAccess: builder.query<ApiResponse<StandaloneCourseAccess>, number | string>({
			query: id => `/standalone-courses/${id}/access`,
			providesTags: (result, error, id) => [{ type: 'STANDALONE_COURSE', id: `access-${id}` }],
		}),
		startStandaloneLecture: builder.mutation<ApiResponse<unknown>, { courseId: number; lectureId: number }>({
			query: ({ courseId, lectureId }) => ({
				url: `/standalone-courses/${courseId}/lectures/${lectureId}/start`,
				method: 'POST',
			}),
			invalidatesTags: (result, error, arg) => [
				{ type: 'STANDALONE_COURSE', id: arg.courseId },
				{ type: 'STANDALONE_COURSE', id: `access-${arg.courseId}` },
			],
			async onQueryStarted(arg, api) {
				try {
					await api.queryFulfilled
					// The lecture is consumed through the classroom-era course cache.
					api.dispatch(CourseApi.util.invalidateTags([{ type: 'GO_TO_COURSE', id: arg.lectureId }]))
				} catch {}
			},
		}),
		checkoutStandaloneCourse: builder.mutation<ApiResponse<CheckoutResponse>, CheckoutStandaloneCoursePayload>({
			query: payload => ({
				url: '/checkout/standalone-course',
				method: 'POST',
				body: payload,
			}),
			async onQueryStarted(arg, api) {
				try {
					await api.queryFulfilled
					// Wallet balance + ownership state (mirrors checkoutSingleCourse).
					api.dispatch(dashboardAPI.util.invalidateTags(['Profile']))
					api.dispatch(
						standaloneCourseApi.util.invalidateTags([
							{ type: 'STANDALONE_COURSE', id: arg.standalone_course_id },
							{ type: 'STANDALONE_COURSE', id: `access-${arg.standalone_course_id}` },
							'STANDALONE_MINE',
							'STANDALONE_FEATURED',
						])
					)
				} catch {}
			},
		}),
		getStandaloneInvoices: builder.query<ApiResponse<StandaloneInvoice[]>, { page?: number; perPage?: number }>({
			query: args => `/standalone-courses/invoices?${toQS(args)}`,
		}),
		// Standalone promocodes have their own endpoint + key (the classroom
		// /promocodes/verify rejects standalone ids).
		verifyStandalonePromocode: builder.mutation<ApiResponse<{ value: number }>, { standalone_course_id: number; code: string }>({
			query: body => ({
				url: '/standalone-courses/promocodes/verify',
				method: 'POST',
				body,
			}),
		}),
	}),
})

export const {
	useGetStandaloneCoursesQuery,
	useGetFeaturedStandaloneCoursesQuery,
	useGetStandaloneCourseDetailsQuery,
	useLazyGetStandaloneCourseDetailsQuery,
	useGetMyStandaloneCoursesQuery,
	useGetStandaloneCourseAccessQuery,
	useStartStandaloneLectureMutation,
	useCheckoutStandaloneCourseMutation,
	useGetStandaloneInvoicesQuery,
	useVerifyStandalonePromocodeMutation,
} = standaloneCourseApi

export default standaloneCourseApi
